import { Box, Divider } from '@mui/material';
import { useQueryStates } from 'nuqs';
import { useState } from 'react';
import { ResetFiltersButton } from './actions/ResetFiltersButton';
import { AddFilterMenu } from './AddFilterMenu';
import { useListViewContext } from './context/ListViewContext';
import { RemovableFilterSlot } from './RemovableFilterSlot';
import { FilterDefinition } from './types';

/**
 * Faceted filter bar component that combines all filters.
 * This component is now purely a rendering engine that loops over the configuration
 * and renders the appropriate components with their props.
 *
 * Implements the hybrid trigger strategy:
 * - Text inputs: debounced (300ms)
 * - Selects: immediate update
 *
 * Manages state for which non-permanent filters are "active" (should be shown even if empty).
 */
export function FilterBar() {
  const { filters, parsers, setFilterValue } = useListViewContext();

  // Read all filter values from URL (for ordering: filters with value but not in activeFilters)
  const [filterValues] = useQueryStates(parsers);

  // Track which non-permanent filters are active (should be shown even if empty)
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set());

  const permanentFilters = filters.filter((f) => f.isPermanent);
  const addableFilters = filters.filter((f) => !f.isPermanent);

  // Only show a filter in the bar when it would actually render (has a displayable value or is active).
  // Treat false, [], and empty string as "no value" so we don't render orphan RemovableFilterSlot when the filter component returns null.
  const hasDisplayableValue = (v: unknown) =>
    v != null && v !== '' && v !== false && (!Array.isArray(v) || v.length > 0);

  // Order: (1) filters with URL value but not explicitly added → config order; (2) activeFilters → addition order (Set keeps insertion order, so just-added is last)
  const group1 = addableFilters.filter(
    (f) => hasDisplayableValue(filterValues[f.name]) && !activeFilters.has(f.name),
  );
  const addableByName = new Map(addableFilters.map((f) => [f.name, f]));
  const group2 = Array.from(activeFilters)
    .map((name) => addableByName.get(name))
    .filter((c): c is FilterDefinition => c != null);
  const orderedAddable = [...group1, ...group2];

  // Callback to add a filter to the active set
  const handleAddFilter = (filterId: string) => {
    setActiveFilters((prev) => new Set(prev).add(filterId));
  };

  // Callback to remove a filter: clear its URL value and remove from the active set
  const handleRemoveFilter = (filterId: string) => {
    setFilterValue(filterId, null);
    setActiveFilters((prev) => new Set([...prev].filter((id) => id !== filterId)));
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap',
      }}
    >
      {/* Permanent filters - always visible, no remove button */}
      {permanentFilters.map((config) => {
        const FilterComponent = config.component;
        return (
          <FilterComponent
            key={config.name}
            name={config.name}
            label={config.label}
            parser={parsers[config.name]}
            {...config.componentProps}
          />
        );
      })}

      <Divider flexItem orientation="vertical" sx={{ mx: 0.5, my: 0.5 }} />

      {/* Addable filters - shown when active or when they have a value. Wrapped in RemovableFilterSlot for external remove button. */}
      {orderedAddable.map((config) => {
        const FilterComponent = config.component;
        const isActive = activeFilters.has(config.name);
        return (
          <RemovableFilterSlot key={config.name} onRemove={() => handleRemoveFilter(config.name)}>
            <FilterComponent
              name={config.name}
              label={config.label}
              parser={parsers[config.name]}
              isActive={isActive}
              {...config.componentProps}
            />
          </RemovableFilterSlot>
        );
      })}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {/* Add filter button - shows menu to add new filters */}
        <AddFilterMenu onAddFilter={handleAddFilter} activeFilterIds={activeFilters} />

        <ResetFiltersButton onReset={() => setActiveFilters(new Set())} />
      </Box>
    </Box>
  );
}
